import React, { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatMonto, formatFecha } from '../utils/format';
import { pref } from '../utils/moneda';
import SelectorMoneda, { useVistaMoneda } from '../components/reportes/SelectorMoneda';
import Exportar from '../components/reportes/Exportar';
import EncabezadoImpresion from '../components/reportes/EncabezadoImpresion';
import BotonAyuda from '../components/BotonAyuda';

// ============================================================
// Flujo de caja proyectado (pendiente #86), siempre de la empresa activa:
// saldo de hoy + cobros esperados − pagos a proveedores, a 7/15/30/90 días,
// contra la suma de los saldos mínimos de las cuentas. Los cobros se esperan
// en el vencimiento más el atraso promedio de cada cliente (12 meses, mínimo
// 3 facturas pagadas). El backend manda USD y BCV; el selector solo cambia la
// vista.
// ============================================================

function Monto({ v, vista, clase = '' }) {
  return <span className={`whitespace-nowrap ${v < 0 ? 'text-red-600' : ''} ${clase}`}>{pref(vista)}{formatMonto(v)}</span>;
}

function TablaDocumentos({ titulo, color, filas, k, vista, parte }) {
  const total = filas.reduce((s, x) => s + x[k], 0);
  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="px-4 py-2.5 flex items-baseline justify-between border-b">
        <h2 className={`font-semibold ${color}`}>{titulo}</h2>
        <span className="text-sm text-slate-500">{filas.length} documento{filas.length === 1 ? '' : 's'} · <Monto v={total} vista={vista} clase="font-semibold text-slate-700" /></span>
      </div>
      {filas.length === 0 ? (
        <p className="px-4 py-3 text-sm text-slate-400">Nada en los próximos 90 días</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="px-3 py-1.5">Fecha esperada</th>
                <th className="px-3 py-1.5">{parte}</th>
                <th className="px-3 py-1.5">Documento</th>
                <th className="px-3 py-1.5">Vence</th>
                <th className="px-3 py-1.5">Criterio</th>
                <th className="px-3 py-1.5 text-right">Monto</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((x) => (
                <tr key={x.id} className="border-t">
                  <td className="px-3 py-1.5 whitespace-nowrap">
                    {formatFecha(x.fecha_esperada)}
                    {x.vencida && <span className="ml-1.5 text-[11px] font-medium bg-red-50 text-red-700 rounded-full px-1.5">vencida</span>}
                  </td>
                  <td className="px-3 py-1.5 text-slate-700">{x.parte}</td>
                  <td className="px-3 py-1.5 text-slate-600 whitespace-nowrap">{x.numero}</td>
                  <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap">{formatFecha(x.fecha_vencimiento)}</td>
                  <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap">
                    {x.origen === 'programada' && 'Agenda de pagos'}
                    {x.origen === 'atraso_cliente' && `Atraso del cliente (${x.atraso > 0 ? '+' : ''}${x.atraso} días)`}
                    {x.origen === 'vencimiento' && 'Vencimiento'}
                  </td>
                  <td className="px-3 py-1.5 text-right"><Monto v={x[k]} vista={vista} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function ReporteFlujoProyectado() {
  const { empresaActiva, empresaActivaNombre } = useAuth();
  const [vista, setVista] = useVistaMoneda();
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!empresaActiva) return;
    setCargando(true);
    setError('');
    api.get('/reportes/flujo-proyectado')
      .then(({ data }) => setDatos(data))
      .catch((err) => setError(err.response?.data?.error || 'No se pudo cargar el flujo proyectado'))
      .finally(() => setCargando(false));
  }, [empresaActiva]);

  const k = vista === 'BCV' ? 'bcv' : 'usd';
  const minimo = datos?.minimo?.[k] || 0;
  const bajo = datos?.bajo_minimo?.[k];

  // Excel: lo mismo que se ve, en la moneda elegida.
  const contenidoExcel = () => {
    const criterio = (x) => (x.origen === 'programada' ? 'Agenda de pagos'
      : x.origen === 'atraso_cliente' ? `Atraso del cliente (${x.atraso > 0 ? '+' : ''}${x.atraso} días)` : 'Vencimiento');
    const docs = (filas, parte) => ({
      columnas: [
        { titulo: 'Fecha esperada', tipo: 'fecha' }, { titulo: parte }, { titulo: 'Documento' },
        { titulo: 'Vence', tipo: 'fecha' }, { titulo: 'Criterio' }, { titulo: `Monto ${vista}`, tipo: 'monto' }
      ],
      filas: filas.map((x) => [x.fecha_esperada, x.parte, x.numero, x.fecha_vencimiento, criterio(x), x[k]]),
      total: ['', '', '', '', 'Total', filas.reduce((t, x) => t + x[k], 0)]
    });
    return {
      titulo: 'Flujo proyectado',
      detalle: `Hoy a 90 días · montos en ${vista}`,
      hojas: [
        {
          nombre: 'Saldos',
          columnas: [{ titulo: 'Horizonte' }, { titulo: 'Fecha', tipo: 'fecha' }, { titulo: 'Cobros', tipo: 'monto' }, { titulo: 'Pagos', tipo: 'monto' }, { titulo: `Saldo ${vista}`, tipo: 'monto' }],
          filas: [
            ['Hoy', null, null, null, datos.saldo_hoy[k]],
            ...datos.horizontes.map((h) => [`A ${h.dias} días`, h.fecha, h.cobros[k], h.pagos[k], h.saldo[k]])
          ]
        },
        { nombre: 'Por cobrar', ...docs(datos.cobros, 'Cliente') },
        { nombre: 'Por pagar', ...docs(datos.pagos, 'Proveedor') }
      ]
    };
  };

  const tarjeta = (titulo, fecha, saldo, cobros, pagos) => {
    const enRojo = minimo > 0 ? saldo < minimo : saldo < 0;
    return (
      <div key={titulo} className={`bg-white rounded-xl shadow-sm p-4 border-l-4 ${enRojo ? 'border-red-500' : 'border-green-500'}`}>
        <p className="text-xs text-slate-500">{titulo}{fecha && <span className="text-slate-400"> · {formatFecha(fecha)}</span>}</p>
        <p className={`text-xl font-bold mt-0.5 ${enRojo ? 'text-red-600' : 'text-slate-800'}`}>{pref(vista)}{formatMonto(saldo)}</p>
        {cobros != null && (
          <p className="text-xs mt-1 space-x-2">
            <span className="text-green-700">+{formatMonto(cobros)}</span>
            <span className="text-red-600">−{formatMonto(pagos)}</span>
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between print:hidden">
        <h1 className="text-2xl font-bold text-slate-800">
          Flujo proyectado
          {empresaActivaNombre && <span className="text-slate-400 font-normal"> · {empresaActivaNombre}</span>}
          <BotonAyuda />
        </h1>
        <div className="flex items-center gap-2 flex-wrap">
          <SelectorMoneda vista={vista} onChange={setVista} />
          <Exportar excel={contenidoExcel} disabled={!datos} />
        </div>
      </div>

      <EncabezadoImpresion titulo="Flujo proyectado" detalle={`Hoy a 90 días · montos en ${vista}`} />

      {error && <p className="text-red-600 text-sm">{error}</p>}

      {cargando ? (
        <p className="text-slate-400 text-sm">Cargando…</p>
      ) : datos && (
        <>
          {!datos.tasa.fecha ? (
            <p className="flex items-center gap-1.5 text-sm text-amber-700">
              <AlertTriangle size={15} />
              No hay tasas cargadas: los montos en Bs no se pueden convertir.
            </p>
          ) : (
            <p className="text-xs text-slate-500">
              Montos convertidos con la tasa del {formatFecha(datos.tasa.fecha)} (BCV {formatMonto(datos.tasa.bcv)} · mercado {formatMonto(datos.tasa.mercado)}).
              {' '}Cobros en el vencimiento más el atraso promedio de cada cliente en los últimos 12 meses (con al menos 3 facturas pagadas). Pagos en su fecha de la agenda o en su vencimiento. Lo vencido se cuenta para hoy.
            </p>
          )}

          {bajo ? (
            <div className="flex items-start gap-2 rounded-lg bg-red-50 text-red-700 px-4 py-2.5 text-sm">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <span>
                El saldo proyectado cae bajo el mínimo ({pref(vista)}{formatMonto(minimo)}) el <b>{formatFecha(bajo.fecha)}</b>
                {bajo.dias > 0 ? `, en ${bajo.dias} día${bajo.dias === 1 ? '' : 's'}` : ', hoy'}: quedaría en {pref(vista)}{formatMonto(bajo.saldo)}.
              </span>
            </div>
          ) : minimo > 0 ? (
            <div className="flex items-center gap-2 rounded-lg bg-green-50 text-green-700 px-4 py-2.5 text-sm">
              <CheckCircle2 size={16} />
              El saldo se mantiene sobre el mínimo ({pref(vista)}{formatMonto(minimo)}) los próximos 90 días.
            </div>
          ) : (
            <p className="text-xs text-slate-500">Ninguna cuenta tiene saldo mínimo definido; se marca en rojo solo si el saldo queda negativo.</p>
          )}

          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
            {tarjeta('Hoy', null, datos.saldo_hoy[k])}
            {datos.horizontes.map((h) => tarjeta(`A ${h.dias} días`, h.fecha, h.saldo[k], h.cobros[k], h.pagos[k]))}
          </div>

          {datos.cuentas_sin_tasa > 0 && (
            <p className="flex items-center gap-1.5 text-sm text-amber-700">
              <AlertTriangle size={15} />
              {datos.cuentas_sin_tasa} cuenta(s) en Bs no se pudieron convertir por falta de tasa y no suman en el saldo de hoy.
            </p>
          )}

          <TablaDocumentos titulo="Por cobrar" color="text-green-700" filas={datos.cobros} k={k} vista={vista} parte="Cliente" />
          <TablaDocumentos titulo="Por pagar" color="text-red-600" filas={datos.pagos} k={k} vista={vista} parte="Proveedor" />
        </>
      )}
    </div>
  );
}
